import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from '../utils/toast';
import { ClockIcon, CurrencyDollarIcon, DocumentTextIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import PasteUploadArea from './PasteUploadArea';

const TimeLogForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  
  const [formData, setFormData] = useState({
    clientId: '',
    ratePerHour: '',
    hours: 0,
    minutes: 0,
    description: '',
    attachments: [],
    date: new Date().toISOString().split('T')[0]
  });

  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.ratePerHour, formData.hours, formData.minutes]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await api.get('/master-data');
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const calculateTotalAmount = () => {
    const rate = parseFloat(formData.ratePerHour) || 0;
    const hours = parseInt(formData.hours) || 0;
    const minutes = parseInt(formData.minutes) || 0;
    const totalHours = hours + (minutes / 60);
    const amount = rate * totalHours;
    setTotalAmount(amount.toFixed(2));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = async (file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const newAttachment = {
          filename: response.data.filename,
          url: response.data.url
        };
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, newAttachment]
        }));
        
        toast.success('File uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.clientId) {
      toast.error('Please select a client');
      return;
    }

    if (!formData.ratePerHour || parseFloat(formData.ratePerHour) <= 0) {
      toast.error('Please enter a valid rate per hour');
      return;
    }

    if (formData.hours === 0 && formData.minutes === 0) {
      toast.error('Please enter hours or minutes');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/time-logs', formData);
      
      if (response.data.success) {
        toast.success('Time log created successfully');
        navigate(`/client/${formData.clientId}`);
      }
    } catch (error) {
      console.error('Error creating time log:', error);
      toast.error(error.response?.data?.message || 'Failed to create time log');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Add Time Log</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track time spent on client work and calculate billing automatically
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Client Information</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Client <span className="text-red-500">*</span>
            </label>
            {loadingClients ? (
              <div className="text-sm text-gray-500">Loading clients...</div>
            ) : (
              <select
                name="clientId"
                value={formData.clientId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a client...</option>
                {clients.map(client => (
                  <option key={client._id} value={client._id}>
                    {client.clientName} {client.customId ? `(${client.customId})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Time & Rate */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-blue-600" />
            Time & Rate
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Rate Per Hour */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rate per Hour ($) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  name="ratePerHour"
                  value={formData.ratePerHour}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            {/* Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hours <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>

            {/* Minutes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minutes <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="minutes"
                value={formData.minutes}
                onChange={handleChange}
                min="0"
                max="59"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>
          </div>

          {/* Total Amount Display */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Total Amount:</span>
              <span className="text-2xl font-bold text-blue-600">${totalAmount}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formData.hours}h {formData.minutes}m × ${formData.ratePerHour || 0}/hr
            </p>
          </div>

          {/* Date */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
            Work Description
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the work completed..."
              required
            />
          </div>
        </div>

        {/* Attachments */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <PaperClipIcon className="h-5 w-5 mr-2 text-blue-600" />
            Attachments (Optional)
          </h2>
          
          <PasteUploadArea
            onFileSelect={handleFileUpload}
            accept="*/*"
            maxSize={10 * 1024 * 1024}
            placeholder="Upload work proof documents (PDF, images, etc.)"
          />

          {/* Attachment List */}
          {formData.attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              {formData.attachments.map((attachment, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                  <div className="flex items-center space-x-3">
                    <PaperClipIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-700">{attachment.filename}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Time Log'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TimeLogForm;

